import { z } from "zod";
import {
  Button,
  Grid,
  Header,
  Icon,
  Message,
  Modal,
  Segment,
} from "semantic-ui-react";
import { AxiosEmployeePositionAPI } from "../../common/Api";
import { Form, FormSelect } from "../../common/components/Form";
import { Formik, useField } from "formik";
import React, { useState } from "react";

import { ChangeButton } from "../../common/components/ChangeButton";
import { ChangeDateInput } from "../../common/components/ChangeDateInput";
import { HeaderItem } from "../../common/components/HeaderItem";
import { Icons } from "../../common/Icons";
import { AxiosPositionsAPI } from "../../positions/Api";
import ReactFocusLock from "react-focus-lock";
import { ReviewAccessInput } from "../../common/components/ReviewAccessInput";
import { ServerErrorMessage } from "../../common/components/ServerErrorMessage";
import { ToastId } from "../../common/ToastIds";
import { toast } from "react-toastify";
import { useAsync } from "react-async";
import { useDebouncedSearch } from "../../utils/Hooks";
import { useSettings } from "../../utils/Settings";

const PositionInfoPanel = ({ header = "Cargo", position }) => {
  return (
    <>
      <Header>{header}</Header>
      <HeaderItem icon={Icons.POSITION} label="Nome" value={position?.name} />
      <HeaderItem
        icon={Icons.SENIORITY_LEVEL}
        label="Nível de Senioridade"
        value={position?.seniority_level?.level}
      />
      <HeaderItem icon={Icons.CODE} label="Código" value={position?.code} />
      <HeaderItem
        label="Categoria"
        icon={Icons.CATEGORY}
        value={position?.category}
      />
      <HeaderItem
        label="Descrição"
        icon={Icons.DESCRIPTION}
        value={position?.description}
      />
    </>
  );
};

const PreviewChangePanel = ({ currentPosition, selectedPosition }) => {
  return (
    <Grid stackable>
      <Grid.Column width={7}>
        <PositionInfoPanel header="Cargo atual" position={currentPosition} />
      </Grid.Column>
      <Grid.Column width={2} verticalAlign={"middle"}>
        <Icon size="big" name={"arrow right"} />
      </Grid.Column>
      <Grid.Column width={7}>
        <PositionInfoPanel header="Novo cargo" position={selectedPosition} />
      </Grid.Column>
    </Grid>
  );
};

const EmployeePositionSelect = ({ onPositionChange, ...props }) => {
  const [, , helpers] = useField(props);

  const [, debouncedSearch, setSearch] = useDebouncedSearch();

  const {
    data: positions,
    isPending,
    error,
  } = useAsync({
    promiseFn: AxiosPositionsAPI.getAll,
    search: debouncedSearch,
    watch: debouncedSearch,
  });

  const handleChange = (e, v) => {
    helpers.setValue(v.value);

    if (onPositionChange) {
      const selectedOption = options.find((e) => e.value === v.value);
      const position = selectedOption ? selectedOption.position : null;
      onPositionChange(position);
    }
  };

  const handleSearch = (e, { searchQuery }) => {
    setSearch(searchQuery);
  };

  let options = positions
    ? positions.results.map((p) => ({
        text: p.name,
        position: p,
        value: p.api_url,
        content: (
          <Header style={{ fontSize: "1rem" }}>
            {p.name}
            <Header.Subheader>
              {p.code} / {p.seniority_level?.level ?? "-"} /{" "}
              {p.category ? p.category : "-"} /{" "}
              {p.description ? p.description : "-"}/ Data de criação:{" "}
              {p.current_record.timespan.lower.format("DD/MM/YYYY").toString()}
            </Header.Subheader>
          </Header>
        ),
      }))
    : [];

  return (
    <Grid.Row>
      <Grid.Column>
        {error && (
          <Message error visible>
            <Message.Content>
              Não foi possível carregar a lista de cargos, por favor tente
              novamente.
            </Message.Content>
          </Message>
        )}
        <FormSelect
          clearable
          scrolling
          loading={isPending}
          onSearchChange={handleSearch}
          onChange={handleChange}
          options={options}
          {...props}
        />
      </Grid.Column>
    </Grid.Row>
  );
};

const PositionChangeSchema = z.object({
  //TODO: Add appropriate employee type
  employee: z
    .object({})
    .passthrough()
    .refine((val) => val !== null && val !== undefined, {
      message: "Employee is required",
    }),
  new_position: z
    .string({
      required_error: "Por favor insira o cargo",
    })
    .min(1, "Por favor insira o cargo"),
  change_date: z.coerce.date({
    required_error: "Por favor insira a data",
    invalid_type_error: "Data inválida",
  }),
  review_access: z.boolean({
    required_error: "Review access é obrigatório",
    invalid_type_error: "Review access deve ser um booleano",
  }),
});

const EmployeePositionChangeForm = ({
  employee,
  currentPosition,
  onSuccess,
  onCancel,
}) => {
  const [selectedPosition, setSelectedPosition] = useState();

  const { run, isPending, isFulfilled, error } = useAsync({
    deferFn: AxiosEmployeePositionAPI.changePosition,
  });

  const { data: dataSettings, isFetching: isAccessPending } = useSettings();

  if (isAccessPending) {
    return null;
  }

  const reviewAccessActive = dataSettings
    ? dataSettings.find((setting) => setting.key === "REVIEW_ACCESS_ACTIVE")
        .value === "True"
    : false;

  const formData = {
    employee: employee,
    new_position: "",
    change_date: "",
    review_access: reviewAccessActive,
  };

  const handleSubmit = (values, { setSubmitting }) => {
    run(values);
    setSubmitting(false);
  };

  if (isFulfilled) onSuccess();

  return (
    <>
      <Formik
        initialValues={formData}
        onSubmit={handleSubmit}
        validate={(values) => {
          const result = PositionChangeSchema.safeParse(values);
          if (!result.success) {
            return result.error.flatten().fieldErrors;
          }
        }}
      >
        <Form loading={isPending}>
          <Segment basic>
            <ServerErrorMessage error={error} />
            <Grid>
              <EmployeePositionSelect
                name="new_position"
                label="Novo cargo"
                onPositionChange={setSelectedPosition}
              />
              <ChangeDateInput />
              {reviewAccessActive && <ReviewAccessInput />}
            </Grid>
          </Segment>
          <Segment basic>
            <PreviewChangePanel
              currentPosition={currentPosition}
              selectedPosition={selectedPosition}
            />
          </Segment>
          <Segment basic>
            <Button type="submit" primary>
              Alterar
            </Button>
            <Button basic onClick={onCancel}>
              Cancelar
            </Button>
          </Segment>
        </Form>
      </Formik>
    </>
  );
};

export const EmployeePositionChangeModal = ({
  employee,
  currentPosition,
  reload,
  trigger,
}) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSuccess = () => {
    handleClose();
    reload();
    toast.success("Cargo alterado com sucesso!", ToastId.POSITION_CHANGE);
  };

  return (
    <Modal
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      onFocus={(e) => e.stopPropagation()}
      trigger={
        trigger || (
          <ChangeButton disabled={!employee._actions.change_position} />
        )
      }
      style={{ maxWidth: "700px" }}
    >
      <Modal.Header>Alterar cargo de {employee.name}</Modal.Header>
      <Modal.Content>
        <ReactFocusLock>
          <EmployeePositionChangeForm
            employee={employee}
            currentPosition={currentPosition}
            onSuccess={handleSuccess}
            onCancel={handleClose}
          />
        </ReactFocusLock>
      </Modal.Content>
    </Modal>
  );
};
